// @ts-nocheck
import { Component } from '@theme/component';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that opens the "Add to your basket" drawer after an
 * add-to-cart succeeds, showing only the item(s) that were just added.
 *
 * The drawer's line items are rendered via a real <cart-items-component>
 * (same one the real cart drawer uses), so quantity/delete controls already
 * work and stay in sync with the rest of the cart without any custom logic
 * here — this component only needs to hide rows that weren't part of the
 * add, and handle opening/closing the drawer itself.
 */
class AddToBasketDrawerComponent extends Component {
  /**
   * The variant ids currently being shown, so the filter can be reapplied
   * whenever this section re-morphs for an unrelated reason (e.g. a quantity
   * change or removal triggered from within this same drawer, or from the
   * real cart drawer) — otherwise the fresh, unfiltered HTML from that morph
   * would bring every cart item back.
   * @type {string[] | null}
   */
  #activeVariantIds = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartUpdate);
  }

  /**
   * @param {CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    const { source, variantIds } = event.detail?.data ?? {};
    const isNewAdd = source === 'product-form-component' && variantIds?.length;

    if (isNewAdd) {
      this.#activeVariantIds = variantIds;
    } else if (this.#activeVariantIds?.length) {
      // Drop any variant that's no longer in the cart (or now at qty 0), so we
      // can tell when everything we were showing has been removed. Driven by
      // the cart data on the event itself rather than DOM row presence, since
      // a same-drawer removal's CSS animation and this event can resolve in
      // either order.
      const cartItems = event.detail?.resource?.items;
      if (Array.isArray(cartItems)) {
        const stillInCart = new Set(
          cartItems.filter((item) => item.quantity > 0).map((item) => item.variant_id.toString())
        );
        this.#activeVariantIds = this.#activeVariantIds.filter((id) => stillInCart.has(id));
      }
    }

    if (!this.#activeVariantIds?.length) {
      if (!isNewAdd) {
        // The underlying cart-items-component just morphed in the full,
        // unfiltered cart (everything else still in the cart). Hide all of
        // it before closing, otherwise those other items flash into view for
        // the moment the close animation plays.
        requestAnimationFrame(() => {
          this.#showOnlyJustAdded([], false);
          this.#closeIfOpen();
        });
      }
      return;
    }

    if (isNewAdd) {
      // Hide whatever's currently in the DOM immediately and synchronously —
      // it could be stale (this page's own initial server render reflecting
      // an older cart, a back/forward-cache restore, etc.) — before doing
      // anything else. Then wait until the row(s) for this add actually
      // exist before revealing the drawer, rather than assuming the
      // cart-items-component's own morph has already finished by "next
      // frame" (a relative-timing guess that's already proven fragile once).
      this.#hideAllRows();
      this.#waitForRowsThenShow(this.#activeVariantIds);
    } else {
      requestAnimationFrame(() => this.#showOnlyJustAdded(this.#activeVariantIds, false));
    }
  };

  #closeIfOpen() {
    const drawer = this.querySelector('custom-drawer');
    if (drawer?.hasAttribute('drawer-open')) drawer.closeDrawer();
  }

  #hideAllRows() {
    const drawer = this.querySelector('custom-drawer');
    if (!drawer) return;

    drawer.querySelectorAll('[ref="cartItemRows[]"]').forEach((row) => {
      row.hidden = true;
      row.style.display = 'none';
    });

    this.querySelector('cart-items-component')?.classList.add('cart-items-component--empty');
  }

  /**
   * Polls (via rAF) until every variant id we're about to show actually has a
   * matching row in the DOM *and* that row's image has finished loading —
   * otherwise the row's text/price/quantity would be correct immediately
   * (just DOM attribute updates) while the <img> keeps showing whatever
   * picture was already loaded there until its new src finishes downloading.
   * Falls back to just showing whatever's there after ~1 second so a genuine
   * failure elsewhere doesn't leave the drawer stuck never opening.
   * @param {string[]} variantIds
   * @param {number} attempt
   */
  #waitForRowsThenShow(variantIds, attempt = 0) {
    const drawer = this.querySelector('custom-drawer');
    if (!drawer) return;

    const matchingRows = [...drawer.querySelectorAll('[ref="cartItemRows[]"]')].filter((row) =>
      variantIds.includes(row.querySelector('[data-variant-id]')?.dataset.variantId)
    );

    const matchingRowVariantIds = new Set(
      matchingRows.map((row) => row.querySelector('[data-variant-id]')?.dataset.variantId)
    );
    const hasAllRows = variantIds.every((id) => matchingRowVariantIds.has(id));
    const imagesReady = matchingRows.every((row) => {
      const img = row.querySelector('img');
      return !img || img.complete;
    });

    if ((hasAllRows && imagesReady) || attempt > 60) {
      this.#showOnlyJustAdded(variantIds, true);
      return;
    }

    requestAnimationFrame(() => this.#waitForRowsThenShow(variantIds, attempt + 1));
  }

  /**
   * @param {string[]} variantIds
   * @param {boolean} openOnApply - Whether this is a fresh add (should close
   *   other drawers and open this one) vs. reapplying an existing filter.
   */
  #showOnlyJustAdded(variantIds, openOnApply) {
    const drawer = this.querySelector('custom-drawer');
    if (!drawer) return;

    let visibleCount = 0;

    drawer.querySelectorAll('[ref="cartItemRows[]"]').forEach((row) => {
      const rowVariantId = row.querySelector('[data-variant-id]')?.dataset.variantId;
      const isJustAdded = variantIds.includes(rowVariantId);

      // `hidden` alone isn't enough here: cart-products.liquid's own stylesheet
      // sets `.cart-items__table-row { display: flex }` as an author rule, which
      // beats the browser's `[hidden] { display: none }` user-agent rule
      // regardless of specificity. Set the inline style directly instead.
      row.hidden = !isJustAdded;
      row.style.display = isJustAdded ? '' : 'none';

      if (isJustAdded) visibleCount++;
    });

    this.querySelector('cart-items-component')?.classList.toggle('cart-items-component--empty', visibleCount === 0);

    if (!openOnApply) return;

    document.querySelectorAll('custom-drawer[drawer-open]').forEach((openDrawer) => {
      if (openDrawer !== drawer) openDrawer.closeDrawer();
    });

    drawer.openDrawer();
  }
}

if (!customElements.get('add-to-basket-drawer-component')) {
  customElements.define('add-to-basket-drawer-component', AddToBasketDrawerComponent);
}
